import { RefObject, useEffect, useState } from "react";

/**
 * Measures the element's own width so the terminal reflows correctly even when
 * it is embedded in a narrower shell than the viewport. Falls back to
 * `window.innerWidth` before the first measurement.
 */
export function useElementWidth(ref: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 1600 : window.innerWidth));

  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      const next = el ? el.clientWidth : window.innerWidth;
      if (next) {
        setWidth((prev) => (prev === next ? prev : next));
      }
    };

    measure();
    window.addEventListener("resize", measure);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && ref.current) {
      observer = new ResizeObserver(measure);
      observer.observe(ref.current);
    }

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [ref]);

  return width;
}

export const TERMINAL_BREAKPOINTS = {
  /** below this the terminal collapses to a single column */
  /** below this the chart series is thinned and gutters tighten */
  narrow: 640,
  stack: 940,
  /** at or above this the terminal runs the full three-column layout */
  wide: 1440,
};
